import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, differenceInCalendarDays, format, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ArrowRight, ChevronLeft, ChevronRight, Loader2, MousePointerClick } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EntityCombobox } from '@/components/custom/entity-combobox';
import { providerPickerConfig, locationPickerConfig } from '@/components/custom/entity-pickers';
import { SlotsView, type SlotsPreview } from '@/components/appointments/SlotsView';
import { useDaySlotsRange } from '@/components/appointments/hooks/use-day-slots';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { HttpError } from '@vagusx/clients';
import type { AppointmentWithRelations, DaySlot, ProviderDaySlots } from '@vagusx/clients';
import { useRescheduleAppointment } from '../hooks/use-reschedule-appointment';
import { useSchedulingContext } from '@/hooks/use-current-organization';
import {
  appointmentMinutes,
  clinicToday,
  initialRescheduleDay,
  overlapsBooking,
  planReschedule,
  slotRefusesType,
  type RescheduleTarget,
} from '../utils/reschedule-plan';

/**
 * Days laid side by side, starting at the shown day: staff are choosing a day for
 * this provider, and a week of their columns shows which days have room at a glance.
 */
const WEEK_DAYS = 7;

interface RescheduleDialogProps {
  open: boolean;
  appointment: AppointmentWithRelations | null;
  onClose: () => void;
  /** Called after a successful reschedule (in addition to closing this dialog) —
   *  lets the parent close the surrounding detail sheet. */
  onRescheduled?: () => void;
}

/**
 * Reschedule dialog — the practice's own calendar, not a patient-style list of
 * days and times. Staff see the day grid they already use on Appointments →
 * Slots (a column per provider at a location, each in its clinic's local time):
 * open slots, the other bookings around them, and the appointment being moved,
 * marked "Current". It opens on the appointment's day, filtered to its provider
 * and location; either filter can be widened. Clicking an open slot (or any
 * empty time) previews the move on the grid; nothing is written until Confirm.
 *   - same provider AND location → native EHR reschedule (a time move, ADR-092).
 *   - different provider/location → book the new time, then cancel the original
 *     (cancel+rebook), since the EHR can't "move" an appointment across providers.
 */
export function RescheduleDialog({
  open,
  appointment,
  onClose,
  onRescheduled,
}: RescheduleDialogProps): React.ReactElement {
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      {/* Full-screen panel: override the centered-modal defaults (position,
          max-width, radius) so the calendar fills the viewport. */}
      <DialogContent className="top-0 left-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:max-w-none">
        {/* Mounted only while open, so each opening starts on the appointment's
            own day and filters, and the day query doesn't run behind a closed dialog. */}
        {appointment && (
          <RescheduleCalendar
            key={appointment.id}
            appointment={appointment}
            onSubmittingChange={setSubmitting}
            onDone={() => {
              onClose();
              onRescheduled?.();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RescheduleCalendar({
  appointment,
  onSubmittingChange,
  onDone,
}: {
  appointment: AppointmentWithRelations;
  onSubmittingChange: (submitting: boolean) => void;
  onDone: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { reschedule } = useRescheduleAppointment();

  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    initialRescheduleDay(appointment.startTime, appointment.location?.timezone)
  );
  const [providerIds, setProviderIds] = useState<string[]>(
    appointment.providerId ? [appointment.providerId] : []
  );
  const [locationIds, setLocationIds] = useState<string[]>(
    appointment.locationId ? [appointment.locationId] : []
  );
  const [target, setTarget] = useState<RescheduleTarget | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const { days, isLoading } = useDaySlotsRange({
    startDate: selectedDate,
    days: WEEK_DAYS,
    providerIds: providerIds.length > 0 ? providerIds : undefined,
    locationIds: locationIds.length > 0 ? locationIds : undefined,
  });
  // Every provider@location column on screen, across the days shown.
  const columns = useMemo(() => days.flatMap((d) => d.data.providers), [days]);
  // "Today" is the clinic's (the appointment's location), like every day here.
  const atToday =
    differenceInCalendarDays(selectedDate, clinicToday(appointment.location?.timezone)) <= 0;
  // The practice's first bookable day (lead time), as the old slot search's `min`.
  const { data: schedulingCtx } = useSchedulingContext();
  const earliestDate = schedulingCtx?.earliestSchedulableDate ?? null;

  const durationMinutes = appointmentMinutes(appointment);
  const currentTz =
    appointment.location?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const patientName = appointment.patient
    ? `${appointment.patient.firstName} ${appointment.patient.lastName}`
    : 'this patient';
  const providerName = appointment.provider
    ? `${appointment.provider.firstName} ${appointment.provider.lastName}`
    : 'Unassigned provider';

  const plan = target ? planReschedule(appointment, target) : null;
  // Both a move and a re-book keep the appointment's type, so a slot that only
  // takes other types can't take either.
  const refusesSlot = (slot: DaySlot): boolean =>
    slotRefusesType(slot, appointment.appointmentTypeId);
  // A warning, not a block: staff may double-book on purpose. Only bookings in the
  // picked provider@location count, and only on the days on screen.
  const overlaps = useMemo(() => {
    if (!target) return false;
    const slots = columns
      .filter((p) => p.provider.id === target.providerId && p.location?.id === target.locationId)
      .flatMap((p) => p.slots);
    return overlapsBooking(target.startTime, durationMinutes, slots, appointment.id);
  }, [target, columns, durationMinutes, appointment.id]);

  const preview = useMemo<SlotsPreview | null>(
    () =>
      target
        ? {
            providerId: target.providerId,
            locationId: target.locationId,
            startTime: target.startTime,
            durationMinutes,
            label: `New · ${formatInTimeZone(target.startTime, target.timezone, 'h:mm a')}`,
            slotId: target.slotId,
          }
        : null,
    [target, durationMinutes]
  );

  const showDate = (date: Date): void => {
    // Never before the clinic's today: past times can't take an appointment.
    const today = clinicToday(appointment.location?.timezone);
    setSelectedDate(startOfDay(date) < today ? today : startOfDay(date));
  };

  // A failed move or booking means the grid was stale: drop the pick and re-read.
  const refreshAfterFailure = (): void => {
    setTarget(null);
    void queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
  };

  const weekLabel = `${format(selectedDate, 'EEE, MMM d')} \u2013 ${format(
    addDays(selectedDate, WEEK_DAYS - 1),
    'EEE, MMM d'
  )}`;

  const handleSlotClick = (
    slot: DaySlot,
    providerId: string,
    location: ProviderDaySlots['location']
  ): void => {
    // Only a free time can take the appointment; a booking (this one included)
    // is someone's time. A locationless column can't be booked into.
    if (slot.status !== 'open' || !location || submittingRef.current) return;
    // Open only for other visit types: the EHR won't take this appointment there,
    // and the move endpoint refuses it (ADR-313). Say so rather than offer it.
    if (refusesSlot(slot)) {
      toast.error("That slot is for other visit types, not this appointment's. Pick another time.");
      return;
    }
    // Inside the practice's lead time: the engine the old search used never
    // offered these, and the move endpoint doesn't check.
    if (
      earliestDate &&
      formatInTimeZone(slot.startTime, location.timezone, 'yyyy-MM-dd') < earliestDate
    ) {
      toast.error(`The earliest bookable day is ${format(parseISO(earliestDate), 'EEE, MMM d')}.`);
      return;
    }
    const column = columns.find(
      (p) => p.provider.id === providerId && p.location?.id === location.id
    );
    setTarget({
      startTime: slot.startTime,
      providerId,
      providerName: column
        ? `${column.provider.firstName} ${column.provider.lastName}`
        : providerName,
      locationId: location.id,
      locationName: location.name,
      timezone: location.timezone,
      appointmentTypeId: slot.appointmentTypeId,
      slotId: slot.id,
    });
  };

  const handleConfirm = async (): Promise<void> => {
    if (!target || !plan || plan.kind === 'unchanged' || submittingRef.current) return;
    // The grid only refuses a past time when it is clicked; a pick can age into
    // the past before Confirm, and the API does not refuse one.
    if (new Date(target.startTime).getTime() <= Date.now()) {
      toast.error('That time has passed. Please pick another.');
      setTarget(null);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    onSubmittingChange(true);
    try {
      if (plan.kind === 'move') {
        // In-place native move — same provider/location, new time only.
        if (await reschedule(appointment.id, target.startTime)) onDone();
        else refreshAfterFailure();
        return;
      }

      // Different provider/location: the EHR can't move the appointment, so book
      // the new time first (a failure leaves the original intact), then cancel it.
      if (!appointment.patientId) {
        toast.error('This appointment has no patient on file; cannot re-book.');
        return;
      }
      try {
        await getAppointmentClient().book({
          patientId: appointment.patientId,
          providerId: target.providerId,
          locationId: target.locationId,
          startTime: target.startTime,
          durationMinutes: plan.durationMinutes,
          appointmentTypeId: plan.appointmentTypeId,
          reasonForVisit: appointment.reasonForVisit ?? undefined,
          bookedVia: 'web',
        });
      } catch (err) {
        const msg =
          err instanceof HttpError || err instanceof Error ? err.message.toLowerCase() : '';
        toast.error(
          msg.includes('available') || msg.includes('slot') || msg.includes('conflict')
            ? 'That time is no longer available. Please pick another.'
            : 'Could not book the new time. The original appointment is unchanged.'
        );
        refreshAfterFailure();
        return;
      }

      // New appointment booked; release the original. If this fails the patient
      // briefly has two appointments — recoverable, so we surface it clearly.
      try {
        await getAppointmentClient().cancel(appointment.id, {
          reason: 'Rescheduled to a different provider',
        });
        toast.success('Appointment rescheduled');
      } catch {
        toast.warning(
          'Booked the new time, but could not cancel the original — cancel it manually.'
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      onDone();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      onSubmittingChange(false);
    }
  };

  return (
    <>
      <DialogHeader className="shrink-0 space-y-1 border-b border-border/50 px-6 py-4 pr-12">
        <DialogTitle>Reschedule {patientName}</DialogTitle>
        <DialogDescription>
          Currently{' '}
          <span className="font-medium text-foreground">
            {formatInTimeZone(appointment.startTime, currentTz, 'EEE, MMM d · h:mm a zzz')}
          </span>{' '}
          with {providerName}
          {appointment.location && ` at ${appointment.location.name}`} ({durationMinutes} min). Pick
          an open slot or any empty time on the calendar.
        </DialogDescription>
      </DialogHeader>

      {/* The week shown, then the filters. Widening provider/location adds columns. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border/50 px-3 py-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Previous 7 days"
          disabled={atToday}
          onClick={() => showDate(addDays(selectedDate, -WEEK_DAYS))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-44 text-center text-sm font-medium tabular-nums">{weekLabel}</span>
        <Button
          variant="outline"
          size="icon"
          aria-label="Next 7 days"
          onClick={() => showDate(addDays(selectedDate, WEEK_DAYS))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => showDate(new Date())}>
          Today
        </Button>
        <div className="w-56 shrink-0">
          <EntityCombobox
            multiple
            config={providerPickerConfig}
            loadStrategy="preload"
            value={providerIds}
            onChange={setProviderIds}
            placeholder="All providers"
            searchPlaceholder="Search providers…"
          />
        </div>
        <div className="w-56 shrink-0">
          <EntityCombobox
            multiple
            config={locationPickerConfig}
            loadStrategy="preload"
            value={locationIds}
            onChange={setLocationIds}
            placeholder="All locations"
            searchPlaceholder="Search locations…"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <SlotsView
          days={days}
          isLoading={isLoading}
          onSlotClick={handleSlotClick}
          currentAppointmentId={appointment.id}
          preview={preview}
          hideDateLabel
          refusesSlot={refusesSlot}
        />
      </div>

      {/* Confirmation bar: what will happen, and the one button that does it. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-border/50 bg-muted/30 px-6 py-3">
        {target && plan ? (
          <>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                {formatInTimeZone(appointment.startTime, currentTz, 'EEE, MMM d · h:mm a')}
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="font-medium">
                {formatInTimeZone(target.startTime, target.timezone, 'EEE, MMM d · h:mm a zzz')}
              </span>
              <span className="text-muted-foreground">
                with {target.providerName} at {target.locationName}
              </span>
              {plan.kind === 'rebook' && (
                <span className="w-full text-xs text-muted-foreground">
                  Different provider or location: books this new time, then cancels the original.
                </span>
              )}
              {overlaps && (
                <span className="w-full text-xs text-amber-700 dark:text-amber-400">
                  At {durationMinutes} min this overlaps another booking in that column.
                </span>
              )}
              {plan.kind === 'unchanged' && (
                <span className="w-full text-xs text-muted-foreground">
                  That is the appointment&rsquo;s current time — pick a different one.
                </span>
              )}
            </div>
            <Button variant="ghost" onClick={() => setTarget(null)} disabled={submitting}>
              Clear
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={submitting || plan.kind === 'unchanged'}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm reschedule
            </Button>
          </>
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <MousePointerClick className="h-4 w-4" />
            Click an open slot or an empty time to choose the new time.
          </p>
        )}
      </div>
    </>
  );
}
